import { useState, useCallback, useEffect } from 'react';
import { t } from '@/lib/i18n/messages';
import { getJuthoorProfitLedger } from '@/lib/api/juthoor-admin';
import type { Locale } from '@/types/auth';
import type { JuthoorProfitLedger } from '@/types/juthoor';

export interface JuthoorProfitLedgerProps {
  initiativeId: string;
  locale: Locale;
}

export function JuthoorProfitLedgerPanel({ initiativeId, locale }: JuthoorProfitLedgerProps) {
  const isRTL = locale === 'ar';
  const [ledger, setLedger] = useState<JuthoorProfitLedger | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const fetchLedger = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const response = await getJuthoorProfitLedger(initiativeId, { page: p, per_page: 20 });
      if (response.success && response.data) {
        setLedger(response.data);
      }
    } finally {
      setLoading(false);
    }
  }, [initiativeId]);

  useEffect(() => {
    fetchLedger(page);
  }, [fetchLedger, page]);

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-12 rounded-xl bg-gray-100 dark:bg-gray-800 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!ledger) return null;

  const { summary, entries, meta } = ledger;

  return (
    <div className={`space-y-4 ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <h3 className="font-semibold text-gray-900 dark:text-white">{t(locale, 'profitLedger')}</h3>

      {/* Summary row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: t(locale, 'totalRevenue'), value: summary.total_revenue },
          { label: t(locale, 'totalPurchaseCost'), value: summary.total_purchase_cost },
          { label: t(locale, 'distributedProfit'), value: summary.distributed },
          { label: t(locale, 'pendingProfit'), value: summary.pending },
        ].map(({ label, value }) => (
          <div
            key={label}
            className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700"
          >
            <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
            <p className="text-base font-bold text-gray-900 dark:text-white mt-0.5">
              {value.toLocaleString(undefined, { maximumFractionDigits: 2 })} NC
            </p>
          </div>
        ))}
      </div>

      {/* Ledger table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-500 dark:text-gray-400 text-xs border-b border-gray-100 dark:border-gray-700">
              <th className="py-2 px-3 font-medium text-start">{isRTL ? 'عدد الأشجار' : 'Trees'}</th>
              <th className="py-2 px-3 font-medium text-start">{isRTL ? 'إجمالي الهامش' : 'Total Margin'}</th>
              <th className="py-2 px-3 font-medium text-start">{isRTL ? 'حصة أثر' : 'Athar'}</th>
              <th className="py-2 px-3 font-medium text-start">{isRTL ? 'حصة الأمانة' : 'Municipality'}</th>
              <th className="py-2 px-3 font-medium text-start">{isRTL ? 'الحالة' : 'Status'}</th>
              <th className="py-2 px-3 font-medium text-start">{isRTL ? 'تاريخ التوزيع' : 'Distributed At'}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
            {entries.map((entry) => (
              <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                <td className="py-2 px-3 font-medium">{entry.tree_count.toLocaleString()}</td>
                <td className="py-2 px-3">{entry.total_margin.toFixed(2)}</td>
                <td className="py-2 px-3">{entry.athar_amount.toFixed(2)}</td>
                <td className="py-2 px-3">{entry.municipality_amount.toFixed(2)}</td>
                <td className="py-2 px-3">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      entry.status === 'distributed'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                    }`}
                  >
                    {entry.status}
                  </span>
                </td>
                <td className="py-2 px-3 text-gray-500">
                  {entry.distributed_at
                    ? new Date(entry.distributed_at).toLocaleDateString()
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {meta.last_page > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-50"
          >
            {isRTL ? 'السابق' : 'Prev'}
          </button>
          <span className="text-sm text-gray-500">
            {page} / {meta.last_page}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
            disabled={page === meta.last_page}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-50"
          >
            {isRTL ? 'التالي' : 'Next'}
          </button>
        </div>
      )}
    </div>
  );
}
